import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {getControlLabel} from '@github-ui/custom-properties-editing'
import type {
  PropertyDefinitionBase,
  PropertySourceDetails,
  SourceInfo,
  SourceType,
} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {ShieldLockIcon} from '@primer/octicons-react'
import {Link as PrimerLink, Truncate} from '@primer/react'
import {memo, type ReactNode, useEffect} from 'react'

import {getDefinitionLabel} from '../helpers/definition-type-label'
import styles from './DefinitionsList.module.css'
import {ListEmptyState} from './ListEmptyState'

const debounceAnnounce = debounce(announce, 300)

interface Props<T extends PropertyDefinitionBase> {
  totalCount: number
  definitions: T[]
  renderItem(definition: T): ReactNode
}
export function DefinitionsList<T extends PropertyDefinitionBase>({totalCount, definitions, renderItem}: Props<T>) {
  const countLabel = `${human(totalCount, {suffix: false})} ${totalCount === 1 ? 'property' : 'properties'}`

  useEffect(() => {
    debounceAnnounce(`${countLabel} found.`)
  }, [definitions, countLabel])

  if (!definitions.length) {
    return <ListEmptyState>No properties have been found</ListEmptyState>
  }

  return (
    <div data-hpc data-testid="repos-definitions-list" className={styles.definitionsListContainer}>
      <ListView
        title="Property definitions"
        metadata={
          <ListViewMetadata
            className="rounded-top-2"
            title={<span className={styles.propertiesCountText}>{countLabel}</span>}
          />
        }
      >
        {definitions.map(renderItem)}
      </ListView>
    </div>
  )
}

interface DefinitionListItemProps {
  definition: PropertyDefinitionBase
  /**
   * Settings level defines if a managed by label is shown.
   * org - label is shown for business properties only
   * business - label is shown for both org and business properties
   * undefined - managed by label is omitted
   */
  settingsLevel?: SourceType
  linkTo: string
}

const DefinitionListItem = memo((props: DefinitionListItemProps) => {
  const {definition, linkTo} = props
  return (
    <>
      <ListItem
        className={styles.listItem}
        title={
          <ListItemTitle
            value={getControlLabel(definition)}
            href="#"
            linkProps={{
              as: Link,
              to: linkTo,
            }}
            containerClassName={styles.ListItemTitle}
          />
        }
        metadata={<DefinitionMetadata {...props} />}
        metadataContainerClassName={styles.metadataContainer}
      >
        <ListItemMainContent>
          {definition.description && (
            <ListItemDescription className={styles.ListItemDescription}>
              <ListItemDescriptionItem className={styles.ListItemDescriptionItem}>
                {definition.description}
              </ListItemDescriptionItem>
            </ListItemDescription>
          )}
        </ListItemMainContent>
      </ListItem>
    </>
  )
})
DefinitionListItem.displayName = 'DefinitionListItem'

function DefinitionMetadata(props: DefinitionListItemProps) {
  const {definition} = props
  return (
    <>
      <ListItemMetadata>
        <span>{getDefinitionLabel(definition.valueType)}</span>
      </ListItemMetadata>
      <ListItemMetadata className={styles.listItemMetadata}>
        <ManagedByLabel {...props} />
      </ListItemMetadata>
    </>
  )
}

function ManagedByLabel({definition, settingsLevel}: DefinitionListItemProps) {
  const {source} = definition
  const managedByLabelComponent = (() => {
    if (settingsLevel === 'org' && source.type === 'business') {
      return <OrgLevelManagedByBusinessLabel business={source} />
    }
    if (settingsLevel === 'business') {
      return <BusinessLevelManagedBySourceLabel propertySource={source} />
    }
  })()

  return (
    managedByLabelComponent && (
      <>
        <span className="mx-2">•</span>
        <div data-testid={`${definition.propertyName}-managed-by-label`} className={styles.managedByLabelContainer}>
          {managedByLabelComponent}
        </div>
      </>
    )
  )
}

function BusinessLevelManagedBySourceLabel({propertySource}: {propertySource: PropertySourceDetails}) {
  const {slug, name, type} = propertySource
  const sourcePath = type === 'business' ? enterprisePath({slug}) : ownerPath({owner: slug})
  return (
    <div className="d-flex flex-row flex-items-center gap-1">
      <span className="color-fg-muted">Managed by </span>
      <PrimerLink inline href={sourcePath}>
        <Truncate title={name}>{name}</Truncate>
      </PrimerLink>
    </div>
  )
}

function OrgLevelManagedByBusinessLabel({business}: {business: SourceInfo}) {
  const {slug, name} = business
  return (
    <div className="d-flex flex-row flex-items-center gap-1">
      <ShieldLockIcon className="color-fg-muted" size={'small'} />
      <span className="color-fg-muted">Managed by </span>
      <PrimerLink inline href={enterprisePath({slug})}>
        <Truncate title={name}>{name}</Truncate>
      </PrimerLink>
    </div>
  )
}

DefinitionsList.Item = DefinitionListItem

try{ DefinitionsList.displayName ||= 'DefinitionsList' } catch {}
try{ DefinitionMetadata.displayName ||= 'DefinitionMetadata' } catch {}
try{ ManagedByLabel.displayName ||= 'ManagedByLabel' } catch {}
try{ BusinessLevelManagedBySourceLabel.displayName ||= 'BusinessLevelManagedBySourceLabel' } catch {}
try{ OrgLevelManagedByBusinessLabel.displayName ||= 'OrgLevelManagedByBusinessLabel' } catch {}